import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import * as _ from 'underscore';
import { BasePaylineEditComponent } from '../base-payline-edit/base-payline-edit.component';
import {
  CompanyDeductionConfig,
  DeductionCalculations
} from '../../../../models/company-deduction-config';
import { Deduction } from '../../../../models/payroll';

@Component({
  selector: 'payline-deduction-row',
  templateUrl: './payline-deduction-row.component.html',
  styleUrls: ['./payline-deduction-row.component.css'],
  standalone: false
})
export class PaylineDeductionRowComponent extends BasePaylineEditComponent implements OnInit {
  @Input() config: CompanyDeductionConfig;
  @Input() deduction: Deduction;
  @Input() clearDeductionsEvent: Observable<void>;
  @Input() rowIndex: number;
  @Input() showHoldPaymentColumn: boolean;
  @Input() lastRowIndex: number;
  @Output() onEdit: EventEmitter<Deduction> = new EventEmitter<Deduction>();

  // form group to manage our user inputs
  public deductionForm = new UntypedFormGroup({
    rateAmount: new UntypedFormControl(''),
    // disable deduction totals via task 8931
    total: new UntypedFormControl({ value: '', disabled: true })
  });

  public overrideAmount: boolean; // option in 2nd column

  get deductionCalculations() {
    return DeductionCalculations;
  }

  constructor() {
    super();
    this._onRateAmountChange = _.debounce(this._onRateAmountChange, 500);
  }

  ngOnInit(): void {
    this.clearDeductionsEvent.subscribe(() => {
      this.deduction.TemporaryAmount = 0;
      this.deduction.TemporaryRate = 0;
      this.deduction.CurrentAmount = 0; // so total field updates immediately
      this.deduction.HoldThirdPartyDisb = false;

      this._setDeductionValues(this.deduction);
    });

    this._setDeductionValues(this.deduction);
    this._configureControls(this.config);
  }

  // NOTE: object reference is broken from the payroll data model.
  // when changes to the current amount are detected,
  // (from Preview Totals), update the total field
  ngDoCheck() {
    // NOTE: when blank, this.deductionForm.get('total') is an empty string
    let value = this.deductionForm.get('total').value || null;
    // CurrentAmount can be null or 0
    let currentAmount = this.deduction.CurrentAmount || null;
    if (currentAmount != value) {
      this.deductionForm.get('total').setValue(this.deduction.CurrentAmount);
    }
  }

  // #region Public Methods

  public onOverrideChange() {
    // task 8940
    const rateCtrl = this.deductionForm.get('rateAmount');
    if (!this.overrideAmount) {
      rateCtrl.setValue(this.deduction.TemporaryRate || this.deduction.PermanentRate || 0);

      // NOTE: when there is no override amount, the TemporaryAmount
      // propery needs to be cleared before sending the calculate payline
      // request, or that property will override the TemporaryRate.
      this.deduction.TemporaryAmount = null;

      // task #25793 - allow edit for fixed dollar override only
      if (this.config.CalculationMethod == DeductionCalculations['Tiered Matching']) {
        this.deductionForm.get('rateAmount').disable();
      }
    } else {
      // Bug 9078: when the user toggles on Override, we must initalize the
      // temp-rate to zero as a starting value.  A temp change is already made,
      // becuase the user is turning on the override - we must ensure that
      // some value gets set in TempAmount in case the user intends the new
      // value to just be zero
      rateCtrl.setValue(this.deduction.TemporaryAmount || 0);
      if (this.deduction.TemporaryAmount === null) {
        this.deduction.TemporaryAmount = 0;
      }

      // task #25793 - allow edit for fixed dollar override only
      if (this.config.CalculationMethod == DeductionCalculations['Tiered Matching']) {
        this.deductionForm.get('rateAmount').enable();
      }
    }

    this.onEdit.emit(this.deduction);
  }

  public onRateAmountChange(evnt): void {
    // attempt to let users to keyboard navigate in the detail editor
    if (this.navigationKeys.includes(evnt.code)) {
      this.onKeyboardNavigate(
        evnt.code,
        this.rowIndex,
        4, // rate/amount column is at focusable column index 4
        this.lastRowIndex
      );
    }

    this._onRateAmountChange(evnt);
  }

  // #endregion

  // #region Private Methods

  private _configureControls(config: CompanyDeductionConfig): void {
    // Tierd-match deductions, user can ONLY override the Total
    if (config.CalculationMethod == DeductionCalculations['Tiered Matching'])
      this.deductionForm.get('rateAmount').disable();
  }

  private _onRateAmountChange(evnt): void {
    // NOTE: if value is blank, pass 0 to the API
    // to ensure totals are returned properly
    let value = evnt?.target?.value || 0;

    if (this.overrideAmount) {
      // task 8940 - if user overrode percentage with fixed amount
      // set value in TemporaryAmount.
      this.deduction.TemporaryAmount = value;
    } else if (
      this.config.IsPercentageDeduction ||
      this.config.CalculationMethod == DeductionCalculations.Rate
    ) {
      this.deduction.TemporaryRate = value;
      this.deduction.TemporaryAmount = null;
    } else {
      // if user changed a fixed-amount deduction, set the temp amount value
      this.deduction.TemporaryAmount = value;
    }

    this.onEdit.emit(this.deduction);
  }

  // update the values for our displayed inputs
  private _setDeductionValues(deduction: Deduction): void {
    // task 8940 - set overrideAmount toggle
    if (
      this.config.CalculationMethod != DeductionCalculations.Fixed &&
      this.deduction.TemporaryAmount != null
    ) {
      // if deduction has a temporary amount,
      // on a non-fixed deduction, toggle override slider.
      this.overrideAmount = true;
    }

    // percentage-based deductions, display the perm rate unless temp is supplied
    if (!this.overrideAmount && this.config.IsPercentageDeduction) {
      this.deductionForm
        .get('rateAmount')
        .setValue(deduction.TemporaryRate ?? deduction.PermanentRate);
    } else {
      // fixed-amount deductions, display the perm amount unless temp is supplied
      this.deductionForm
        .get('rateAmount')
        .setValue(deduction.TemporaryAmount ?? deduction.PermanentAmount);
    }

    this.deductionForm.get('total').setValue(deduction.CurrentAmount);
  }

  // #endregion
}
